import {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, FormControl, Stack } from '@mui/material';
import ModalTemplate from '../modal/modal-template';
import { BaseModalRef } from '@/app/helpers/types';
import { useOpen } from '@/app/hooks';
import { UIFormLabel } from '../ui/ui-form-label';
import { FormTextInput } from '../inputs/form-text-input';
import {
  useCreateFactorsBranchMutation,
  useUpdateFactorsBranchMutation,
  useGetFactorsBranchInfoQuery,
} from '@/app/api';
import { getFetchEror } from '@/app/helpers';
import { useNotification } from '../contexts/NotificationContext';

interface Props {
  onSuccess?: () => void;
  editBranchId?: number | null;
}

interface FormValues {
  branchName: string;
  branchAddress: string;
  managingDirector_cyrl: string;
  managingDirector_latn: string;
  account: string;
  dateOfPowerOfAttorney: string;
  numberOfPowerOfAttorney: string;
}

const DEFAULT_FORM_VALUES: FormValues = {
  branchName: '',
  branchAddress: '',
  managingDirector_cyrl: '',
  managingDirector_latn: '',
  account: '',
  dateOfPowerOfAttorney: '',
  numberOfPowerOfAttorney: '',
};

export const BranchModal = forwardRef<BaseModalRef, Props>(
  ({ onSuccess, editBranchId }, ref) => {
    const { t } = useTranslation();
    const { open, setOpen } = useOpen();
    const { showNotification } = useNotification();
    const [createFactorsBranch] = useCreateFactorsBranchMutation();
    const [updateFactorsBranch] = useUpdateFactorsBranchMutation();

    const isEditMode = !!editBranchId;

    const { data: branchData, isLoading: isLoadingBranch } =
      useGetFactorsBranchInfoQuery(
        { id: editBranchId! },
        {
          skip: !editBranchId,
          refetchOnMountOrArgChange: true,
        }
      );

    const { reset, control, handleSubmit } = useForm<FormValues>({
      defaultValues: DEFAULT_FORM_VALUES,
    });

    const formData = useMemo(() => {
      if (!branchData?.data) return DEFAULT_FORM_VALUES;

      return {
        branchName: branchData.data.branchName || '',
        branchAddress: branchData.data.branchAddress || '',
        managingDirector_cyrl: branchData.data.managingDirector_cyrl || '',
        managingDirector_latn: branchData.data.managingDirector_latn || '',
        account: branchData.data.account || '',
        dateOfPowerOfAttorney: branchData.data.dateOfPowerOfAttorney || '',
        numberOfPowerOfAttorney: branchData.data.numberOfPowerOfAttorney || '',
      };
    }, [branchData?.data]);

    useEffect(() => {
      if (isEditMode && open && branchData?.data) {
        reset(formData);
      }
    }, [isEditMode, open, branchData?.data, formData, reset]);

    useImperativeHandle(
      ref,
      () => ({
        open: onOpen,
        close: onClose,
      }),
      []
    );

    const onOpen = useCallback(() => {
      setOpen(true);
    }, []);

    const onClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        reset(DEFAULT_FORM_VALUES);
      }, 300);
    }, [reset]);

    const onSubmit = useCallback(
      async (values: FormValues) => {
        try {
          if (isEditMode && editBranchId) {
            await updateFactorsBranch({
              id: editBranchId,
              body: values,
            }).unwrap();
            showNotification(
              t('settings.branch_successfully_updated'),
              'success'
            );
          } else {
            await createFactorsBranch(values).unwrap();
            showNotification(
              t('settings.branch_successfully_added'),
              'success'
            );
          }
          onSuccess?.();
        } catch (error) {
          showNotification(getFetchEror(error), 'error');
        } finally {
          onClose();
        }
      },
      [
        isEditMode,
        editBranchId,
        updateFactorsBranch,
        createFactorsBranch,
        showNotification,
        t,
        onSuccess,
        onClose,
      ]
    );

    const title = useMemo(
      () =>
        isEditMode
          ? t('settings.branch_modal.edit_title')
          : t('settings.branch_modal.title'),
      [isEditMode, t]
    );

    const submitButtonText = useMemo(
      () =>
        isEditMode
          ? t('settings.branch_modal.save')
          : t('settings.branch_modal.add'),
      [isEditMode, t]
    );

    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            maxWidth: 600,
            width: '100%',
          },
        }}
      >
        <ModalTemplate
          onClose={onClose}
          title={title}
          submitBtnText={submitButtonText}
          cancelBtnText={t('settings.cancel')}
          onSubmit={handleSubmit(onSubmit)}
          loading={isEditMode && isLoadingBranch}
        >
          <Stack spacing={2}>
            <FormControl required>
              <UIFormLabel sx={{ color: '#0B1370' }}>
                {t('settings.branch_modal.name')}:
              </UIFormLabel>
              <FormTextInput
                control={control}
                name='branchName'
                placeholder={t('settings.branch_modal.not_filled')}
                required
              />
            </FormControl>

            <FormControl required>
              <UIFormLabel sx={{ color: '#0B1370' }}>
                {t('settings.branch_modal.address')}:
              </UIFormLabel>
              <FormTextInput
                control={control}
                name='branchAddress'
                placeholder={t('settings.branch_modal.not_filled')}
                required
              />
            </FormControl>

            <FormControl required>
              <UIFormLabel sx={{ color: '#0B1370' }}>
                {t('settings.branch_modal.managing_director_cyrl')}:
              </UIFormLabel>
              <FormTextInput
                control={control}
                name='managingDirector_cyrl'
                placeholder={t('settings.branch_modal.not_filled')}
                required
              />
            </FormControl>

            <FormControl required>
              <UIFormLabel sx={{ color: '#0B1370' }}>
                {t('settings.branch_modal.managing_director_latn')}:
              </UIFormLabel>
              <FormTextInput
                control={control}
                name='managingDirector_latn'
                placeholder={t('settings.branch_modal.not_filled')}
                required
              />
            </FormControl>

            <FormControl>
              <UIFormLabel sx={{ color: '#0B1370' }}>
                {t('settings.branch_modal.account')}:
              </UIFormLabel>
              <FormTextInput
                control={control}
                name='account'
                placeholder={t('settings.branch_modal.not_filled')}
              />
            </FormControl>
            <Stack direction='row' spacing={2}>
              <FormControl required>
                <UIFormLabel sx={{ color: '#0B1370' }}>
                  {t('settings.branch_modal.power_of_attorney_number')}:
                </UIFormLabel>
                <FormTextInput
                  control={control}
                  name='numberOfPowerOfAttorney'
                  placeholder={t('settings.branch_modal.not_filled')}
                  required
                />
              </FormControl>
              <FormControl required>
                <UIFormLabel sx={{ color: '#0B1370' }}>
                  {t('settings.branch_modal.power_of_attorney_date')}:
                </UIFormLabel>
                <FormTextInput
                  control={control}
                  name='dateOfPowerOfAttorney'
                  placeholder='DD.MM.YYYY'
                  required
                  rules={{
                    pattern: {
                      value: /^\d{2}\.\d{2}\.\d{4}$/,
                      message: t('settings.branch_modal.invalid_date'),
                    },
                  }}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    const formatted = [
                      digits.slice(0, 2),
                      digits.slice(2, 4),
                      digits.slice(4, 8),
                    ]
                      .filter((p) => p)
                      .join('.');
                    const field = e.target as HTMLInputElement;
                    field.value = formatted;
                  }}
                />
              </FormControl>
            </Stack>
          </Stack>
        </ModalTemplate>
      </Dialog>
    );
  }
);
